/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// State
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';
import {
	TUser,
	TUserQueryParams,
	TParsedUser,
} from '../types';
import { TSelectOption } from '@global-types/index';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface IUserSlice {
	/* Pagination */
	usersTotalPages: number;
	usersPage: number;
	usersTotal: number;
	usersPageSize: number;

	/* Datatable */
	usersSelectedRecords: TParsedUser[];
	usersDefaultSelectedFields: TSelectOption[];
	usersOpenConfirmationModalDelete: {
		open: boolean;
		params: {
			id?: string;
		};
	};
	usersQueryParams: TUserQueryParams;
	usersSortStatus: Partial<TUser>;
	usersSortParams: string;
	usersSelectedRow: Partial<TParsedUser>;
	usersLastAccessTokenCreated: string | null;
	apiTokenOrganization: string | null;
	openDrawerUserForm: boolean;
	paramsFromProject: {
		project?: string;
		user?: string;
		producer?: string;
	};
	setParamsFromProject: (_paramsFromProject: {
		project?: string;
		user?: string;
		producer?: string;
	}) => void;

	setUsersSelectedRow: (
		_usersSelectedRow: Partial<TParsedUser>
	) => void;

	setUserOrganization: (
		_organizationId: string | null
	) => void;
	setUsersPage: (_usersPage: number) => void;
	setUsersSelectedRecords: (
		_usersSelectedRecords: TParsedUser[]
	) => void;
	setUsersOpenConfirmationModalDelete: (_usersOpenConfirmationModalDelete: {
		open: boolean;
		params: {
			id?: string;
		};
	}) => void;
	setUsersQueryParams: (
		_usersQueryParams: TUserQueryParams
	) => void;
	setUsersSortStatus: (
		_usersSortStatus: Partial<TUser>
	) => void;
	setUsersSortParams: (_usersSortParams: string) => void;

	setUsersPageSize: (_pageSize: number) => void;
	setUsersDefaultSelectedFields: (
		_usersDefaultSelectedFields: TSelectOption[]
	) => void;
	setOpenDrawerUserForm: (
		_openDrawerUserForm: boolean
	) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

const initialState = {
	/* Pagination */
	usersTotal: 0,
	usersPage: 1,
	usersPageSize: 10,
	usersTotalPages: 0,
	/**************/
	openDrawerUserForm: false,
	users: [],
	usersSelectedRecords: [],
	usersDefaultSelectedFields: [],
	usersQueryParams: {},
	usersSelectedRow: {},
	usersSortParams: '-createdAt',
	apiTokenOrganization: null,
	usersSortStatus: {},
	usersLastAccessTokenCreated: null,
	paramsFromProject: {},
	usersOpenConfirmationModalDelete: {
		id: '',
		open: false,
		params: '',
	},
	usersOpenConfirmationModalVerify: {
		open: false,
		params: '',
	},
	usersOpenConfirmationModalMint: {
		open: false,
		params: '',
	},
};

export const createUsersSlice: StateCreator<
	TStore,
	[],
	[],
	IUserSlice
> = (set) => ({
	...initialState,
	setOpenDrawerUserForm: (_openDrawerUserForm: boolean) =>
		set({
			openDrawerUserForm: _openDrawerUserForm,
		}),
	setUsersDefaultSelectedFields: (
		_usersDefaultSelectedFields: TSelectOption[]
	) =>
		set({
			usersDefaultSelectedFields:
				_usersDefaultSelectedFields,
		}),
	setUsersSelectedRow: (
		_usersSelectedRow: Partial<TParsedUser>
	) =>
		set({
			usersSelectedRow: _usersSelectedRow,
		}),
	setUserOrganization: (_organizationId: string | null) =>
		set({ apiTokenOrganization: _organizationId }),
	setUsersLastAccessTokenCreated: (
		_usersLastAccessTokenCreated: string
	) =>
		set({
			usersLastAccessTokenCreated:
				_usersLastAccessTokenCreated,
		}),
	setParamsFromProject: (_paramsFromProject) =>
		set({ paramsFromProject: _paramsFromProject }),
	setUsersSortParams: (_sortStatus: string) =>
		set({ usersSortParams: _sortStatus }),
	setUsersSortStatus: (_sortStatus: Partial<TUser>) =>
		set({ usersSortStatus: _sortStatus }),
	setUsersQueryParams: (_queryParams: TUserQueryParams) =>
		set({ usersQueryParams: _queryParams }),
	setUsersTotal: (_total: number) =>
		set({ usersTotal: _total }),
	setUsersOpenConfirmationModalDelete:
		(_usersOpenConfirmationModalDelete: {
			open: boolean;
			params: {
				id?: string;
			};
		}) =>
			set({
				usersOpenConfirmationModalDelete:
					_usersOpenConfirmationModalDelete,
			}),
	setUsersPage: (_page: number) =>
		set({ usersPage: _page }),
	setUsersPageSize: (_pageSize: number) =>
		set({ usersPageSize: _pageSize }),
	setUsersTotalPages: (_totalPages: number) =>
		set({ usersTotalPages: _totalPages }),
	setUsersSelectedRecords: (
		_usersSelectedRecords: TParsedUser[]
	) =>
		set({
			usersSelectedRecords: _usersSelectedRecords,
		}),
});
